import React from 'react'

import { RHFInput } from 'react-hook-form-input'
import * as yup from 'yup'
import useForm from 'react-hook-form'
import helpers from 'helpers'

import {
  Paper,
  Box,
  Typography,
  Divider,
  TextField,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Collapse,
  Chip,
  RadioGroup,
  Radio,
} from '@material-ui/core'
import CachedIcon from '@material-ui/icons/CachedOutlined'

import { Autocomplete } from '@material-ui/lab'

import schemas from './../../schemas'
import ContractsDialogSetPenalty from '../ContractsDialogSetPenalty'
import useFetch from 'hooks/useFetch'
import * as service from 'service'
import useContractConfigs from 'hooks/useContractConfigs'

const ContractsEditForm = ({ contract, generalSettings, onSubmit }) => {
  const [open, setOpen] = React.useState(false)
  const [penaltyId, setPenaltyId] = React.useState(contract.penaltyId)
  const [contractPenalty, setContractPenalty] = React.useState()
  const [
    isLoadingContractPenalty,
    setIsloadingContractPenalty,
  ] = React.useState(false)

  const { register, setValue, handleSubmit, errors, watch } = useForm({
    validationSchema: schemas.contractConfig,
    defaultValues: {
      dayExpiration: contract.dayExpiration,
      urlNotification: contract.urlNotification || '',
      settings: {
        daysToOverdueInvoice: String(contract.settings.daysToOverdueInvoice),
        generateServiceTaxInvoices:
          contract.settings.generateServiceTaxInvoices,
        fixedTaxInvoiceDescription:
          contract.settings.fixedTaxInvoiceDescription || '',
        invoiceInterval: helpers.formatNumber.formatNumberToString(
          contract.settings.invoiceInterval,
        ),
        daysToGenerateNfe: helpers.formatNumber.formatNumberToString(
          contract.settings.daysToGenerateNfe,
        ),
      },
      contractEmails: contract.contractEmails.map(
        contractEmail => contractEmail.email,
      ),
    },
  })
  const [confirm, setConfirm] = React.useState(false)

  const { response, isLoading } = useFetch(service.recurrence.penalties.get, {
    penaltyId: 'last',
  })

  const disablePenaltyUpdate =
    response?.data?.penalty?.interestRate ===
      contractPenalty?.data?.penalty?.interestRate &&
    response?.data?.penalty?.fineValue ===
      contractPenalty?.data?.penalty?.fineValue &&
    response?.data?.penalty?.finePercentage ===
      contractPenalty?.data?.penalty?.finePercentage

  const settings = watch('settings')
  const contractEmails = watch('contractEmails')

  const {
    checkSettings,
    handleChangeServiceTax,
    handleChangeSettings,
  } = useContractConfigs(settings, setValue)

  React.useEffect(() => {
    const getContractPenalty = async () => {
      setIsloadingContractPenalty(true)

      await service.recurrence.penalties
        .get({ penaltyId })
        .then(response => {
          setContractPenalty(response)
        })
        .catch(() => {})
        .finally(() => {
          setIsloadingContractPenalty(false)
        })
    }

    getContractPenalty()
  }, [penaltyId])

  React.useEffect(() => {
    register({ name: 'contractEmails' })
  }, [register, setValue])

  const handleChangeContractEmails = (event, contractEmails) => {
    if (contractEmails.length > 3) {
      return
    }

    setValue(
      'contractEmails',
      contractEmails.filter(email => {
        try {
          yup
            .string()
            .email()
            .validateSync(email, {})

          return true
        } catch (error) {
          return false
        }
      }),
    )
  }

  return (
    <>
      <Paper>
        <form onSubmit={handleSubmit(onSubmit)} id="form-update">
          <Box p={2}>
            <Typography variant="h4">Configurações da assinatura</Typography>
          </Box>

          <Divider />

          <Box p={2}>
            <Box mb={6}>
              <Box mb={2} mt={2}>
                <Typography variant="h5" color="textSecondary">
                  Assinatura
                </Typography>
              </Box>
              <Box
                mt={2}
                display="flex"
                flexDirection={{
                  xs: 'column',
                  sm: 'row',
                }}
              >
                <Box mr={5} width="100%">
                  <RHFInput
                    as={
                      <TextField
                        label="Dia de vencimento da assinatura"
                        margin="dense"
                        type="number"
                        error={!!errors.dayExpiration}
                        helperText={
                          errors?.dayExpiration?.message ||
                          'Dia base para emissão e vencimento das faturas recorrentes.'
                        }
                        inputProps={{ min: 1, max: 31 }}
                        fullWidth
                      />
                    }
                    name="dayExpiration"
                    register={register}
                    setValue={setValue}
                    mode="onChange"
                  />
                </Box>
                <RHFInput
                  as={
                    <TextField
                      label="URL para envio de notificações"
                      margin="dense"
                      error={!!errors.urlNotification}
                      helperText={
                        errors?.urlNotification?.message ||
                        'Envio de notificações/atualizações para o sistema de gestão do vendedor nos casos de integração por API.'
                      }
                      fullWidth
                    />
                  }
                  name="urlNotification"
                  register={register}
                  setValue={setValue}
                  mode="onChange"
                />
              </Box>

              <Autocomplete
                size="small"
                value={contractEmails}
                options={[]}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      {...getTagProps({ index })}
                    />
                  ))
                }
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Emails adicionais de notificação"
                    variant="outlined"
                    margin="dense"
                    helperText="Informe até 3 e-mails adicionais para receber as faturas e demais e-mails de notificação sobre a assinatura."
                    fullWidth
                  />
                )}
                onChange={handleChangeContractEmails}
                autoSelect
                freeSolo
                multiple
              />
            </Box>

            <Box mb={6}>
              <Box mb={2} mt={2}>
                <Typography variant="h5" color="textSecondary">
                  Faturas
                </Typography>
              </Box>
              <Box mt={2}>
                {!isLoading && !isLoadingContractPenalty && (
                  <>
                    <Box my={3}>
                      <Box
                        display="flex"
                        flexDirection={{
                          xs: 'column',
                          sm: 'row',
                        }}
                      >
                        <Box mr={5} width="100%">
                          <Typography color="textSecondary">
                            Juros ao mês para faturas em atraso
                          </Typography>
                          <RHFInput
                            as={
                              <TextField
                                margin="dense"
                                label="Juros ao mês"
                                fullWidth
                                disabled
                                helperText={
                                  <>
                                    <Box
                                      component="span"
                                      style={{ color: '#546e7a' }}
                                    >
                                      Configuração atual de juros para todas as
                                      assinaturas:{' '}
                                    </Box>
                                    <Box
                                      component="span"
                                      fontWeight="fontWeightBold"
                                      style={{ color: '#546e7a' }}
                                    >
                                      {`${response?.data?.penalty
                                        ?.interestRate || '0'}%`}
                                    </Box>
                                  </>
                                }
                              />
                            }
                            defaultValue={`${contractPenalty?.data?.penalty
                              ?.interestRate || '0'}%`}
                            register={register}
                            setValue={setValue}
                            name="fineValue"
                          />
                        </Box>
                        <Box width="100%">
                          <Typography color="textSecondary">
                            Taxa fixa de multa para faturas pagas após o
                            vencimento
                          </Typography>
                          <RHFInput
                            as={
                              <TextField
                                label="Taxa de multa"
                                margin="dense"
                                disabled
                                fullWidth
                                helperText={
                                  <>
                                    <Box
                                      component="span"
                                      style={{ color: '#546e7a' }}
                                    >
                                      Configuração atual de multa para todas as
                                      assinaturas:{' '}
                                    </Box>
                                    <Box
                                      component="span"
                                      fontWeight="fontWeightBold"
                                      style={{ color: '#546e7a' }}
                                    >
                                      {response?.data?.penalty?.fineValue
                                        ? helpers.currency(
                                            response?.data?.penalty?.fineValue,
                                          )
                                        : `${response?.data?.penalty
                                            ?.finePercentage || '0'}%`}
                                    </Box>
                                  </>
                                }
                              />
                            }
                            defaultValue={
                              contractPenalty?.data?.penalty?.fineValue
                                ? helpers.currency(
                                    contractPenalty?.data?.penalty?.fineValue,
                                  )
                                : `${
                                    contractPenalty?.data?.penalty
                                      ?.finePercentage
                                      ? contractPenalty?.data?.penalty
                                          ?.finePercentage + '%'
                                      : '0'
                                  }`
                            }
                            name="interestRate"
                            register={register}
                            setValue={setValue}
                          />
                        </Box>
                      </Box>
                      <Box mt={1}>
                        <Button
                          disabled={disablePenaltyUpdate}
                          color="primary"
                          startIcon={<CachedIcon />}
                          onClick={() => setOpen(true)}
                        >
                          Atualizar juros e multa
                        </Button>
                      </Box>
                    </Box>
                    <ContractsDialogSetPenalty
                      open={open}
                      setOpen={setOpen}
                      penalty={response?.data?.penalty}
                      setPenaltyId={setPenaltyId}
                    />
                  </>
                )}
                <Box
                  display="flex"
                  flexDirection={{
                    xs: 'column',
                    sm: 'row',
                  }}
                >
                  <Box mr={5} width="100%">
                    <RHFInput
                      as={
                        <TextField
                          label="Dias para vencimento da fatura"
                          margin="dense"
                          type="number"
                          error={!!errors['settings.daysToOverdueInvoice']}
                          helperText={
                            errors['settings.daysToOverdueInvoice']?.message ||
                            'Dias que serão adicionados para o vencimento das faturas, com base no vencimento da assinatura. Ex: se a assinatura tem vencimento todo dia 15 e for configurado 5 dias para vencimento da fatura, o vencimento de cada fatura será todo dia 20 do mês.'
                          }
                          inputProps={{ min: 0 }}
                          fullWidth
                        />
                      }
                      name="settings.daysToOverdueInvoice"
                      register={register}
                      setValue={setValue}
                      mode="onChange"
                    />
                  </Box>
                  <Box width="100%">
                    <RHFInput
                      as={
                        <TextField
                          label="Dias para geração da fatura antes do vencimento da assinatura"
                          type="number"
                          margin="dense"
                          error={!!errors['settings.invoiceInterval']}
                          helperText={
                            errors['settings.invoiceInterval']?.message
                          }
                          inputProps={{ min: 0 }}
                          disabled={checkSettings['invoiceInterval']}
                          fullWidth
                        />
                      }
                      register={register}
                      setValue={setValue}
                      name="settings.invoiceInterval"
                      mode="onChange"
                    />
                    <FormControlLabel
                      control={<Checkbox />}
                      label={`Utilizar configuração geral do vendedor (${generalSettings.invoiceInterval} dias)`}
                      checked={checkSettings['invoiceInterval']}
                      onChange={event =>
                        handleChangeSettings(
                          event,
                          'invoiceInterval',
                          contract.settings.invoiceInterval,
                        )
                      }
                    />
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box mb={6}>
              <Box mb={2} mt={2}>
                <Typography variant="h5" color="textSecondary">
                  Notas fiscais
                </Typography>
              </Box>

              <Typography variant="h6" color="textSecondary">
                Deseja emitir notas fiscais das faturas?
              </Typography>
              <RHFInput
                as={
                  <RadioGroup
                    aria-label="settings.generateServiceTaxInvoices"
                    name="settings.generateServiceTaxInvoices"
                    row
                  >
                    <FormControlLabel
                      value="true"
                      control={<Radio />}
                      label="Sim"
                      checked={settings.generateServiceTaxInvoices}
                    />
                    <FormControlLabel
                      value="false"
                      control={<Radio />}
                      label="Não"
                      checked={!settings.generateServiceTaxInvoices}
                    />
                  </RadioGroup>
                }
                name="settings.generateServiceTaxInvoices"
                register={register}
                setValue={setValue}
                onChange={handleChangeServiceTax}
                mode="onChange"
              />

              <Collapse in={settings.generateServiceTaxInvoices}>
                <Box mb={2}>
                  <RHFInput
                    as={
                      <TextField
                        label="Dias para geração da NFe antes do vencimento da fatura"
                        type="number"
                        margin="dense"
                        error={!!errors['settings.daysToGenerateNfe']}
                        helperText={
                          errors['settings.daysToGenerateNfe']?.message
                        }
                        inputProps={{ min: 0 }}
                        disabled={checkSettings['daysToGenerateNfe']}
                        fullWidth
                      />
                    }
                    register={register}
                    setValue={setValue}
                    name="settings.daysToGenerateNfe"
                    mode="onChange"
                  />
                  <FormControlLabel
                    control={<Checkbox />}
                    label={`Utilizar configuração geral do vendedor (${
                      generalSettings.generateNfeOnInvoiceCreation
                        ? 'Na criação da fatura'
                        : generalSettings.daysToGenerateNfe + ' dias'
                    })`}
                    checked={checkSettings['daysToGenerateNfe']}
                    onChange={event =>
                      handleChangeSettings(
                        event,
                        'daysToGenerateNfe',
                        contract.settings.daysToGenerateNfe,
                      )
                    }
                  />
                </Box>
                <RHFInput
                  as={
                    <TextField
                      label="Descrição fixa nas notas fiscais"
                      margin="dense"
                      type="text"
                      error={!!errors['settings.fixedTaxInvoiceDescription']}
                      helperText={
                        errors['settings.fixedTaxInvoiceDescription']?.message
                      }
                      multiline
                      rows={2}
                      fullWidth
                    />
                  }
                  name="settings.fixedTaxInvoiceDescription"
                  register={register}
                  setValue={setValue}
                  mode="onChange"
                />
              </Collapse>
            </Box>
          </Box>

          <Divider />

          <Box p={2}>
            <Button
              variant="contained"
              type="button"
              onClick={() => setConfirm(true)}
              color="primary"
            >
              Salvar
            </Button>
          </Box>
        </form>
        <Dialog open={confirm} onClose={() => setConfirm(false)}>
          <DialogTitle id="form-dialog-title">Alterar assinatura?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Atenção: Ao realizar uma mudança de Dia de Vencimento de uma
              assinatura, A próxima fatura recorrente será gerada logo no dia de
              vencimento mais próximo respeitando a periodicidade, gerando
              proporcional ou não pela configuração de "Proporcional em mudança
              de dia de faturamento".
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              color="primary"
              variant="contained"
              type="submit"
              form="form-update"
              onClick={() => setConfirm(false)}
            >
              Confirmar
            </Button>
            <Button
              color="primary"
              variant="outlined"
              onClick={() => setConfirm(false)}
            >
              Cancelar
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </>
  )
}

export default ContractsEditForm
